import "@ui5/webcomponents-icons/dist/action-settings.js";
import "@ui5/webcomponents-icons/dist/bar-chart.js";
import "@ui5/webcomponents-icons/dist/batch-payments.js";
import "@ui5/webcomponents-icons/dist/calendar.js";
import "@ui5/webcomponents-icons/dist/car-rental.js";
import "@ui5/webcomponents-icons/dist/letter.js";
import "@ui5/webcomponents-icons/dist/line-chart.js";
import "@ui5/webcomponents-icons/dist/map.js";
import "@ui5/webcomponents-icons/dist/money-bills.js";
import "@ui5/webcomponents-icons/dist/move.js";
import "@ui5/webcomponents-icons/dist/outbox.js";
import "@ui5/webcomponents-icons/dist/shield.js";
import "@ui5/webcomponents-icons/dist/signature.js";
import "@ui5/webcomponents-icons/dist/wallet.js";
import "@ui5/webcomponents-icons/dist/wrench.js";
import type { ParseKeys } from "i18next";

/**
 * Level-3 groups of the side navigation (UX11): the less-used screens of a long module are tagged with one of these
 * keys and gathered under it, below the module's everyday screens. Label `shell:navGroups.<key>`; order of the keys
 * here is the order of the groups in a module.
 */
export const navigationSubGroups = {
  specialMovements: "move",
  billing: "money-bills",
  agreements: "signature",
  notes: "letter",
  guarantees: "shield",
  pettyCash: "wallet",
  followUp: "wrench",
  fieldService: "map",
  carCards: "car-rental",
  analysis: "line-chart",
  reports: "bar-chart",
  distribution: "outbox",
  periodEnd: "calendar",
  setupTools: "batch-payments",
  setup: "action-settings",
} as const;

export type NavigationSubGroup = keyof typeof navigationSubGroups;

/**
 * Navigation registry entry (ADR-0010/D-2). Each frontend module exports its entries; labels are i18n keys of the
 * `shell` namespace (or `dev:` keys for the dev-only samples), never literal strings. Visibility is deny by default:
 * a leaf needs `permission` (granted), `public` (any signed-in user) or `operatorOnly`; groups need a visible child.
 * Groups nest at most twice: module group (level 1) → screens and level-3 groups (level 2) → screens (level 3).
 */
export interface NavigationEntry {
  key: string;
  labelKey: ParseKeys<["shell", "dev"]>;
  icon: string;
  path?: string;
  children?: NavigationEntry[];
  /** Permission key required to see the entry (ADR-0003/D-4). */
  permission?: string;
  /** Visible to every signed-in user (e.g. Home, My profile). */
  public?: boolean;
  /** Visible to platform operators only (operator console). */
  operatorOnly?: boolean;
  /** Plan module key (ADR-0003/D-5). */
  module?: string;
  /** Level-3 group of a less-used screen (UX11); {@link nestSubGroups} moves the screen into it. */
  subGroup?: NavigationSubGroup;
}

/**
 * A module with more screens than this shows its tagged screens in level-3 groups (UX11); a module whose visible
 * screens (after permissions) fit within it is shown flat ({@link compactNavigation}).
 */
export const subGroupThreshold = 8;

/**
 * Builds a module group's level-3 groups from the `subGroup` tags of its screens: untagged screens stay on level 2 in
 * their order, followed by one group per tag (order of {@link navigationSubGroups}) with the tagged screens in their
 * order. Group keys are `<module key>:<sub-group>`.
 */
export function nestSubGroups(group: NavigationEntry): NavigationEntry {
  const children = group.children ?? [];
  if (!children.some((child) => child.subGroup)) {
    return group;
  }
  const direct = children.filter((child) => !child.subGroup);
  const nested = (Object.keys(navigationSubGroups) as NavigationSubGroup[]).flatMap((subGroup): NavigationEntry[] => {
    const members = children.filter((child) => child.subGroup === subGroup);
    return members.length === 0
      ? []
      : [
          {
            key: `${group.key}:${subGroup}`,
            labelKey: `navGroups.${subGroup}`,
            icon: navigationSubGroups[subGroup],
            subGroup,
            children: members,
          },
        ];
  });
  return { ...group, children: [...direct, ...nested] };
}

/** Screens of a navigation subtree (entries with a path), depth first. */
export function navigationLeaves(entries: readonly NavigationEntry[]): NavigationEntry[] {
  return entries.flatMap((entry) => (entry.children ? navigationLeaves(entry.children) : entry.path ? [entry] : []));
}

/**
 * Shows a module flat when its visible screens fit within {@link subGroupThreshold} (UX11): level 3 exists to shorten
 * long modules, so a user who sees only a few screens of a module (permissions) is not made to open a sub-group.
 */
export function compactNavigation(entries: readonly NavigationEntry[]): NavigationEntry[] {
  return entries.map((entry) => {
    const children = entry.children;
    if (!children?.some((child) => child.children) || navigationLeaves(children).length > subGroupThreshold) {
      return entry;
    }
    const direct = children.filter((child) => !child.children);
    const nested = children.flatMap((child) => child.children ?? []);
    return { ...entry, children: [...direct, ...nested] };
  });
}

/** `{ subGroup }` for an entry built in a loop, or nothing when the entry stays on level 2. */
export function inSubGroup(subGroup: NavigationSubGroup | undefined): Pick<NavigationEntry, "subGroup"> {
  return subGroup ? { subGroup } : {};
}
